/**
 * SongGenerator
 */
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components/macro';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Api } from 'store/api';
import {
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton
} from '@mui/material';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faSparkles, faUndo, faRedo } from '@fortawesome/pro-solid-svg-icons';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { faMusic, faAlignLeft } from '@fortawesome/pro-regular-svg-icons';
import { useRecentJobsPanel } from 'app/components/RecentJobsSidepanel/hooks/useRecentJobsPanel';

// Components
import { CheckboxUpsellModal } from 'app/components/CheckboxUpsellModal';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { UpdateUsernameModal } from 'app/components/updateUsernameModal';
import { CreateSamplerStyleModal } from 'app/components/CreateSamplerStyleModal/Loadable';
import { PricingTable } from 'app/pages/UpgradePage/pricingTable';
import { MusicStyleInput } from 'app/components/MusicStyleInput';
import { useAudioFilesListUploader } from 'app/hooks/useAudioFilesListUploader';
import { RecentJobsSidepanel } from 'app/components/RecentJobsSidepanel';
import { GlobalAudioPlayer } from 'app/components/GlobalAudioPlayer';

// Hooks and utilities
import { useInterval } from 'app/hooks/useInterval';
import { useJobsSlice } from 'app/pages/JobsPage/slice';
import { selectJobs } from 'app/pages/JobsPage/slice/selectors';

// Types
interface UserProfile {
  plan?: string;
  inference_credits?: number;
  username?: string;
}

export function SongGenerator() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Redux and context - properly typed
  const { actions: jobActions } = useJobsSlice();
  const jobs = useSelector(selectJobs) || []; // Handle undefined case
  const [userProfile, isLoaded, isSignedIn, refetchUser] = useOutletContext() as [
    UserProfile | null,
    boolean,
    boolean,
    () => Promise<void>
  ];

  // Form state
  const [formTabValue, setFormTabValue] = useState('Full Song');
  const [lyrics, setLyrics] = useState('');
  const [musicStyle, setMusicStyle] = useState('');

  // For lyrics history redoing and undoing
  const [lyricsHistory, setLyricsHistory] = useState<string[]>([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  // Advanced options
  const [enableAdvancedOptions, setEnableAdvancedOptions] = useState(false);
  const [targetBpm, setTargetBpm] = useState(120);
  const [targetKey, setTargetKey] = useState('C');
  const [targetScale, setTargetScale] = useState('Major');

  // UI state
  const [isCheckboxUpsellOpen, setIsCheckboxUpsellOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isUpdateUsernameModalOpen, setIsUpdateUsernameModalOpen] = useState(false);
  const [isSamplerStyleModalOpen, setIsSamplerStyleModalOpen] = useState(false);
  const [isLoadingInference, setIsLoadingInference] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isImprovingLyrics, setIsImprovingLyrics] = useState(false);
  const { isPanelOpen, setPanelOpen, fetchJobs } = useRecentJobsPanel();

  // Validation and errors
  const [error, setError] = useState('');
  const [lyricsError, setLyricsError] = useState('');

  // Init audioFiles from hook
  const {
    audioFiles: vocalReferenceFiles,
    totalUploadProgress: vocalUploadProgress,
    isUploading: isVocalUploading,
    handleLoadedMetadata: handleVocalLoadedMetadata,
    setAudioFiles: setVocalReferenceFiles,
    isAllUploadsComplete: isVocalUploadsComplete,
    getTotalDuration: getVocalTotalDuration,
  } = useAudioFilesListUploader()

  const vocalFilesError = (
    vocalReferenceFiles.length > 0 &&
    vocalUploadProgress === 1 &&
    getVocalTotalDuration() < 5
  ) ? "Too short. Your audio has to be over 15 seconds." : ''

  const {
    audioFiles: musicReferenceFiles,
    totalUploadProgress: musicUploadProgress,
    isUploading: isMusicUploading,
    handleLoadedMetadata: handleMusicLoadedMetadata,
    setAudioFiles: setMusicReferenceFiles,
    isAllUploadsComplete: isMusicUploadsComplete,
    getTotalDuration: getMusicTotalDuration,
  } = useAudioFilesListUploader()

  const musicFilesError = (
    musicReferenceFiles.length > 0 &&
    musicUploadProgress === 1 &&
    getMusicTotalDuration() < 5
  ) ? "Too short. Your audio has to be over 15 seconds." : ''

  // Subscription and credits
  const isCreator = userProfile?.plan?.includes('creator') ?? false;
  const isStarter = userProfile?.plan?.includes('starter') ?? false;
  const inferenceCredits = userProfile?.inference_credits ?? 0;
  const insufficientCreditsForInfer = !isCreator && inferenceCredits < 100;

  // Fetch recent jobs
  const fetchRecentJobs = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return;
    try {
      const responseBody = await Api.fetchRecentSongGenerationJobs();
      dispatch(jobActions.updateJobs(responseBody.jobs));
      await refetchUser();
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
    }
  }, [isLoaded, isSignedIn, dispatch, jobActions, refetchUser]);

  // Form validation
  const isValid = () => {
    console.log('Validating form...');
    setError('');
    setLyricsError('');

    // Validate lyrics for Full Song mode
    if (formTabValue === 'Full Song' && !lyrics.trim()) {
      setLyricsError('Lyrics are required for generating a full song with vocals');
      return false;
    }

    // Validate reference files if uploaded
    if (vocalReferenceFiles.length > 0) {
      if (!isVocalUploadsComplete()) {
        setError('Vocal reference files are still uploading');
        return false;
      }
      if (vocalFilesError) {
        setError(vocalFilesError);
        return false;
      }
    }
    if (musicReferenceFiles.length > 0) {
      if (!isMusicUploadsComplete()) {
        setError('Music reference files are still uploading');
        return false;
      }
      if (musicFilesError) {
        setError(musicFilesError);
        return false;
      }
    }

    // Validate music style for instrumental
    if (formTabValue === 'Instrumental' && !musicStyle.trim()) {
      setError('Music style is required for instrumental generation');
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!isValid()) {
      setIsConfirmOpen(false);
      setIsCheckboxUpsellOpen(false);
      return;
    }

    // Close modals
    setIsConfirmOpen(false);
    setIsCheckboxUpsellOpen(false);
    setIsLoadingInference(true);

    try {
      const vocalReferenceFilenames = vocalReferenceFiles.map(file => ({
        filename: file.filename,
        storageBucket: file.storageBucket,
        storageFileName: file.storageFileName,
      }));
      const musicReferenceFilenames = musicReferenceFiles.map(file => ({
        filename: file.filename,
        storageBucket: file.storageBucket,
        storageFileName: file.storageFileName,
      }));

      const formData = {
        vocalReferenceFiles: vocalReferenceFilenames,
        musicReferenceFiles: musicReferenceFilenames,
        inferenceKwargs: {
          lyrics: formTabValue === 'Full Song' ? lyrics : '',
          music_style: musicStyle,
          song_type: formTabValue,
          ...(enableAdvancedOptions && {
            target_bpm: targetBpm,
            target_key: `${targetKey} ${targetScale.toLowerCase()}`,
          }),
        }
      };

      const result = await Api.createSongGenerationJob(formData);
      console.log('Song generation job created:', result);

      await fetchJobs(Api, true);
      setPanelOpen(true);
      await fetchRecentJobs();

      // Show success message or redirect
      // navigate('/app/jobs');

    } catch (error) {
      console.error('Error creating song generation job:', error);
      setError('Something went wrong, please try again.');
      setTimeout(() => setError(''), 15000);
    } finally {
      setIsLoadingInference(false);
    }
  };

  const handleClickGenerate = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isValid()) return;

    // If Creator, submit directly
    if (isCreator) {
      handleSubmit();
    } else if (!isStarter && !isCreator || (isStarter && insufficientCreditsForInfer)) {
      setIsCheckboxUpsellOpen(true);
    } else {
      setIsConfirmOpen(true);
    }
  };

  const handleUpgrade = () => {
    navigate('/app/upgrade');
  };

  const createQueueAndStackForAudioPlayback = (current_song_job_uuid: string) => {
    // Find index of this job uuid to get the index of the next job
    const currentSongJobIndex = jobs.findIndex(
      j => j.uuid == current_song_job_uuid,
    );
    if (currentSongJobIndex >= jobs.length - 1) return;

    // TODO add updateQueue and updateStack actions to create array of DisplaySong objects and do 2 dispatch calls total
    for (let i = 0; i < jobs.length; i++) {
      // Create a DisplaySongInfo object for all other jobs in list
      if (i == currentSongJobIndex) continue;
      const nextJob = jobs[i];

      // COPIED FROM from JobCard
      const voice_model = nextJob.voice_model;
      let audio_file_outputs = nextJob.audio_file_outputs;
      const audio_file_inputs = nextJob.audio_file_inputs;
      const vocalAudioFiles = audio_file_outputs.filter(
        audio_file_output => audio_file_output.type == 'vocal-inference',
      );
      const mixAudioFiles = audio_file_outputs.filter(
        audio_file_output => audio_file_output.type == 'mixed',
      );
      const input_url = nextJob.func_kwargs?.input_url;
      const should_extract_lead_vocal =
        nextJob.func_kwargs?.should_extract_lead_vocal;

      // COPIED FROM from JobCard
      // Decide which audio file is the main one
      // If standard vocal conversion, choose first output audio file
      let mainAudioFileUrl =
        vocalAudioFiles.length > 0 && vocalAudioFiles[0].audio_file?.public_url;
      // If song conversion (we extracted lead vocal), choose mixed output audio file
      if (should_extract_lead_vocal)
        mainAudioFileUrl =
          mixAudioFiles.length > 0 && mixAudioFiles[0].audio_file?.public_url;

      // COPIED FROM JobCard
      let jobName =
        audio_file_outputs.length > 0 &&
        audio_file_outputs[0].audio_file.filename.split('-').pop().slice(12);
      if (input_url && audio_file_inputs)
        jobName =
          (audio_file_inputs.length > 0 &&
            audio_file_inputs[0].audio_file?.original_filename) ||
          input_url;

      // Create DisplaySong object for next job
      const nextDisplaySong = {
        title: jobName,
        singer: voice_model?.name,
        url: mainAudioFileUrl,
        coverUrl: voice_model?.image_url || "https://storage.googleapis.com/controlla-public-images/ControllaVoiceLogo.png",
      };

      // TODO do this after the loop in one update action
      if (i < currentSongJobIndex) {
        // Add to historyStack
        console.log('prevSong: ', nextDisplaySong);
        // dispatch(globalAudioActions.addSongToHistoryStack(nextDisplaySong));
      } else if (i > currentSongJobIndex) {
        // Add to queue
        console.log('nextSong: ', nextDisplaySong);
        // dispatch(globalAudioActions.queueSong(nextDisplaySong));
      }
    }
  };

  const handleFormTabChange = (e: React.SyntheticEvent, value: string) => {
    if (value !== null && value !== formTabValue) {
      setFormTabValue(value);
      setLyricsError('');
      setError('');
    }
  };

  const pushLyricsToHistory = () => {
    // Store previous lyrics in history
    const newHistory = [...lyricsHistory, lyrics];
    setLyricsHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length);
  }

  const handleImproveLyrics = async () => {
    if (!lyrics.trim()) {
      setLyricsError('Please enter some lyrics to improve');
      return;
    }

    setIsImprovingLyrics(true);
    setLyricsError('');

    try {
      const response = await Api.improveLyrics({
        text: lyrics,
        model: 'gpt-3.5-turbo',
        max_tokens: 1000,
        temperature: 0.7,
      });

      if (response.status === 'success' && response.response) {
        pushLyricsToHistory();
        setLyrics(response.response);
      } else {
        setLyricsError('Failed to improve lyrics. Please try again.');
      }
    } catch (error) {
      console.error('Error improving lyrics:', error);
      setLyricsError('Failed to improve lyrics. Please try again.');
    } finally {
      setIsImprovingLyrics(false);
    }
  };


  const handleUndo = () => {
    if (currentHistoryIndex == lyricsHistory.length) {
      pushLyricsToHistory();
    }
    if (currentHistoryIndex > 0) {
      const newIndex = currentHistoryIndex - 1;
      setCurrentHistoryIndex(newIndex);
      setLyrics(lyricsHistory[newIndex]);
    }
  };

  const handleRedo = () => {
    if (currentHistoryIndex < lyricsHistory.length - 1) {
      const newIndex = currentHistoryIndex + 1;
      setCurrentHistoryIndex(newIndex);
      setLyrics(lyricsHistory[newIndex]);
    }
  };

  // Handle manual lyrics changes - reset history if user edits manually
  const handleLyricsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const limitedText = e.target.value.slice(0, 1000);
    setLyrics(limitedText);
    setLyricsError('');

    // If user manually edits lyrics, clear the history after current position
    if (currentHistoryIndex >= 0 && currentHistoryIndex < lyricsHistory.length - 1) {
      setLyricsHistory(lyricsHistory.slice(0, currentHistoryIndex + 1));
    }
  };

  // Effects
  useEffect(() => {
    fetchRecentJobs();
  }, [fetchRecentJobs]);

  useEffect(() => {
    if (!isSignedIn) {
      setVocalReferenceFiles([]);
      setMusicReferenceFiles([]);
      setIsLoadingInference(false);
      setError('');
      setLyricsError('');
      setPanelOpen(false);
      setIsConfirmOpen(false);
      setIsCheckboxUpsellOpen(false);
      setIsPolling(false);
    }
  }, [isSignedIn]);

  useEffect(() => {
    if (isSignedIn && userProfile && isLoaded && !userProfile.username) {
      setIsUpdateUsernameModalOpen(true);
    }
  }, [isSignedIn, userProfile, isLoaded]);

  // Poll for job updates - safely handle jobs array
  useInterval(async () => {
    if (isPolling) fetchRecentJobs();

    const hasActiveJobs = Array.isArray(jobs) && jobs.some(job =>
      job && job.status !== 'COMPLETED' && job.status !== 'FAILED'
    );

    if (hasActiveJobs && !isPolling) {
      setIsPolling(true);
    } else if (!hasActiveJobs && isPolling) {
      setIsPolling(false);
    }
  }, 15000);

  // Tab UI
  const formTabsElement = (
    <ToggleButtonGroup
      exclusive
      value={formTabValue}
      onChange={handleFormTabChange}
      color="primary"
      aria-label="song generation type"
      sx={{
        marginBottom: '20px',
        marginTop: '10px',
        padding: 0,
        justifyContent: 'space-between',
        borderRadius: 0,
        '& .MuiToggleButton-root': {
          width: '50%',
          flexGrow: 0,
          borderRadius: '15px',
          textTransform: 'uppercase',
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: 'Raleway',
          '@media only screen and (max-width: 600px)': {
            fontSize: '16px',
            padding: '8px 16px',
          }
        }
      }}
      size="small"
      fullWidth={true}
    >
      <ToggleButton value="Full Song">
        <FontAwesomeIcon icon={faAlignLeft} size="sm" style={{marginRight: '8px'}} />
        Full Song
      </ToggleButton>
      <ToggleButton value="Instrumental">
        <FontAwesomeIcon icon={faMusic} size="sm" style={{marginRight: '8px'}} />
        Instrumental
      </ToggleButton>
    </ToggleButtonGroup>
  );

  // Create description strings for modals
  const getCheckboxUpsellDescription = () => {
    if (insufficientCreditsForInfer) {
      return `This will cost 100 credits and you have ${inferenceCredits} ${isStarter ? 'daily ' : ''}credits remaining. Please upgrade for unlimited generations.`;
    }
    return `This will cost 100 credits and you have ${inferenceCredits} credits remaining.`;
  };

  return (
    <>
      <Helmet>
        <title>Create Music - Song Generator</title>
        <meta
          name="Controlla Song Generator"
          content="Create music in any voice, language, or style"
        />
      </Helmet>

      {/* Modals */}
      <CheckboxUpsellModal
        open={isCheckboxUpsellOpen}
        onClose={() => setIsCheckboxUpsellOpen(false)}
        onCancel={() => setIsCheckboxUpsellOpen(false)}
        onConfirm={handleSubmit}
        confirmButtonText={!insufficientCreditsForInfer ? 'Continue with free trial' : ''}
        title={insufficientCreditsForInfer ? "You're out of credits" : "Upgrade for unlimited access to all tools"}
        description={getCheckboxUpsellDescription()}
      />

      <ConfirmationDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleSubmit}
        confirmButtonText="Generate Song"
      >
        <h2>Please confirm:</h2>
        <p style={{textAlign: 'center'}}>
          This will cost 100 credits, and you have {inferenceCredits} credits remaining.
        </p>
        <p
          onClick={handleUpgrade}
          style={{textAlign: 'center', cursor: 'pointer', textDecoration: 'underline', color: '#ff74e9'}}
        >
          Upgrade to Creator for unlimited generations now
        </p>
      </ConfirmationDialog>

      <UpdateUsernameModal
        open={isUpdateUsernameModalOpen}
        onClose={() => setIsUpdateUsernameModalOpen(false)}
      />

      <CreateSamplerStyleModal
        isOpen={isSamplerStyleModalOpen}
        onClose={() => setIsSamplerStyleModalOpen(false)}
        initialData={{ frontendTab: formTabValue }}
      />

      <InferLayoutWrapper
        initial={{ translateY: '100px', opacity: 0 }}
        animate={{ translateY: '0px', opacity: 1 }}
        exit={{ translateY: '0px', opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <InferFormContainer>
          <InferFormContainerContent>
            {userProfile && isLoaded && isSignedIn && !isCreator && !isStarter && !inferenceCredits && (
              <UpsellOverlay>
                <h2>Upgrade for access to Unlimited Song Generation</h2>
                <div style={{display: 'flex', height: 'auto', justifyContent: 'center'}}>
                  <PricingTable
                    small
                    hideFreePlan
                    hideContractCard={false}
                    hideCurrentPlan
                  />
                </div>
              </UpsellOverlay>
            )}

            <HeaderSection>
              <h2 style={{marginTop: isMobile ? '0px' : '20px', marginBottom: '0px'}}>
                Create Music
              </h2>
              <h3 style={{
                width: 'calc(100% - 20px)',
                marginLeft: '10px',
                marginRight: '10px',
                lineHeight: '1.25em',
                marginBottom: isMobile ? '0px' : '10px',
                fontSize: isMobile ? '16px' : '20px',
                marginTop: isMobile ? '0px' : '10px'
              }}>
                in any voice, language, or style
              </h3>
            </HeaderSection>

            <InferForm>
              <FormTabsContainer>
                {/* <FormTabsTitle>What would you like to generate?</FormTabsTitle> */}
                {formTabsElement}
              </FormTabsContainer>

              {/* Reference Track Upload */}
              {/* <InferFormLabel>STYLE REFERENCE (OPTIONAL)</InferFormLabel>
              <InferFormLabelDescription>
                Upload a reference track to match its style. Must be longer than 15 seconds.
              </InferFormLabelDescription> */}


              {/* Lyrics input for Full Song */}
              {formTabValue === 'Full Song' && (
                <LyricsContainer>
                  <InferFormLabel>WRITE LYRICS</InferFormLabel>
                  <TextField
                    value={lyrics}
                    onChange={handleLyricsChange}
                    rows={5}
                    multiline
                    inputProps={{ maxLength: 1000 }}
                    error={!!lyricsError}
                    helperText={lyricsError}
                    placeholder="Enter the lyrics for your song. Use brackets like [chorus] or [verse] for structure."
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        paddingBottom: '50px',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '15px',
                        fontFamily: 'Raleway',
                        fontWeight: '400',
                        width: '100%',
                      },
                    }}
                  />

                  {/* Improve Lyrics Button */}
                  <LyricsButtonsFooter>
                    {/* Undo/Redo Buttons */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <HistoryButton
                        type="button"
                        onClick={handleUndo}
                        disabled={currentHistoryIndex <= 0}
                        title="Undo"
                      >
                        <FontAwesomeIcon icon={faUndo} size="sm" />
                      </HistoryButton>
                      <HistoryButton
                        type="button"
                        onClick={handleRedo}
                        disabled={currentHistoryIndex >= lyricsHistory.length - 1}
                        title="Redo"
                      >
                        <FontAwesomeIcon icon={faRedo} size="sm" />
                      </HistoryButton>
                      {/* {lyricsHistory.length > 0 && (
                        <span style={{
                          fontSize: '12px',
                          color: 'rgba(245, 225, 255, 0.5)',
                          fontFamily: 'Raleway'
                        }}>
                          {currentHistoryIndex + 1}/{lyricsHistory.length}
                        </span>
                      )} */}

                    {/* Improve Lyrics Button */}
                    <ImproveLyricsButton
                      type="button"
                      onClick={handleImproveLyrics}
                      disabled={isImprovingLyrics || !lyrics.trim()}
                    >
                      <FontAwesomeIcon icon={faSparkles} size="sm" />
                      {isImprovingLyrics ? 'Improving...' : 'Improve Lyrics'}
                    </ImproveLyricsButton>
                    </div>
                    <CharacterCount>
                      <span style={{
                        color: lyrics?.length >= 1000 ? '#ff4d4f' : 'rgba(245, 225, 255, 0.74)'
                      }}>
                        {lyrics?.length || 0}/1000 characters
                      </span>
                    </CharacterCount>
                  </LyricsButtonsFooter>
                </LyricsContainer>
              )}

              <MusicStyleInput
                onAudioFilesChange={setMusicReferenceFiles}
                audioFiles={musicReferenceFiles}
                onLoadedMetadata={handleMusicLoadedMetadata}
                audioKeyOffset={1}
                hideLinkInput
                totalUploadProgress={musicUploadProgress}
                error={musicFilesError}
                showVocalStyle
                onAudioVocalStyleFilesChange={setVocalReferenceFiles}
                audioVocalStyleFiles={vocalReferenceFiles}
                onLoadedMetadataVocalStyle={handleVocalLoadedMetadata}
                audioKeyOffsetVocalStyle={0}
                totalUploadProgressVocalStyle={vocalUploadProgress}
                errorVocalStyle={vocalFilesError}
              />

              {/* Advanced Options */}
              <FormControlLabel
                control={
                  <Switch
                    checked={enableAdvancedOptions}
                    onChange={(e) => setEnableAdvancedOptions(e.target.checked)}
                  />
                }
                label="Advanced Options"
                sx={{ marginTop: '20px', marginBottom: '10px' }}
              />

              {enableAdvancedOptions && (
                <AdvancedOptionsContainer>
                  <FormControl style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                    <FormLabel style={{ minWidth: 'fit-content' }}>BPM</FormLabel>
                    <TextField
                      type="number"
                      value={targetBpm}
                      onChange={(e) => setTargetBpm(Number(e.target.value))}
                      size="small"
                      style={{ width: '140px' }}
                      InputProps={{
                        sx: {
                          '& input': {
                            textAlign: 'center',
                            '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0
                            },
                            '-moz-appearance': 'textfield',
                          }
                        },
                        startAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              size="small"
                              onClick={() => setTargetBpm(prev => Math.max(40, prev - 1))}
                            >
                              <FontAwesomeIcon icon={faMinus} size='2xs'/>
                            </IconButton>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setTargetBpm(prev => Math.min(200, prev + 1))}
                            >
                              <FontAwesomeIcon icon={faPlus} size='2xs' />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </FormControl>

                  <FormControl style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                    <FormLabel style={{ minWidth: 'fit-content' }}>Key</FormLabel>
                    <Select
                      value={targetKey}
                      onChange={(e) => setTargetKey(e.target.value)}
                      size="small"
                    >
                      {['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'].map(note => (
                        <MenuItem key={note} value={note}>{note}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                    <FormLabel style={{ minWidth: 'fit-content' }}>Scale</FormLabel>
                    <Select
                      value={targetScale}
                      onChange={(e) => setTargetScale(e.target.value)}
                      size="small"
                    >
                      <MenuItem value="Major">Major</MenuItem>
                      <MenuItem value="Minor">Minor</MenuItem>
                    </Select>
                  </FormControl>
                </AdvancedOptionsContainer>
              )}

              {/* Error Display */}
              {error && (
                <Typography
                  color="#FD6C6E"
                  sx={{
                    mb: 2,
                    fontSize: '14px',
                    fontFamily: 'Raleway',
                    textAlign: 'center',
                  }}
                >
                  {error}
                </Typography>
              )}

              <InferFormFooter>
                <Typography
                  fontSize={14}
                  fontFamily={'Raleway'}
                  color={'#FF74E9'}
                  fontWeight={300}
                >
                  {!isCreator && (
                    <>
                      {'Estimated credits: 100'}
                      <br />
                    </>
                  )}
                  (Unlimited Generations for Creator Plan)
                </Typography>
                <MainButton
                  onClick={handleClickGenerate}
                  disabled={isLoadingInference}
                >
                  {isLoadingInference ? 'Generating...' : 'Create Now'}
                </MainButton>
              </InferFormFooter>
            </InferForm>
          </InferFormContainerContent>
        </InferFormContainer>
        {isSignedIn && (
          <RecentJobsSidepanel
            isOpen={isPanelOpen ?? false}
            onOpenChange={setPanelOpen}
            onJobPlayed={createQueueAndStackForAudioPlayback}
            Api={Api}
            inferenceCredits={inferenceCredits}
            isCreator={isCreator}
            isStarter={isStarter}
            navigate={navigate}
          />
        )}
        <GlobalAudioPlayer />
      </InferLayoutWrapper>
    </>
  );
}

// Styled Components
const InferLayoutWrapper = styled(motion.div)`
  height: calc(100dvh - 70px);
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: stretch;
  background-color: #0c0810;
  overflow-y: auto;

  @media only screen and (max-width: 600px) {
    padding-top: 0px;
    flex-direction: column;
  }
`;

const InferFormContainer = styled.div`
  position: relative;
  height: calc(100dvh - 70px);
  flex-basis: 0;
  flex-grow: 3;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  background-color: #0c0810;
  padding-top: 20px;
  overflow-y: auto;

  @media only screen and (max-width: 600px) {
    padding-top: 0px;
  }
`;

const InferFormContainerContent = styled.div`
  position: relative;
  padding-bottom: 40px;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const UpsellOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 100;
  text-align: left;
  background: #0c0810dd;
  padding: 40px;
  border-radius: 20px;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

const InferForm = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: left;
  padding: 0px 40px 40px;
  border-radius: 40px;
  align-self: stretch;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 40px;
  max-width: 720px;
  width: 100%;
  z-index: 10;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

  @media only screen and (max-width: 600px) {
    padding: 20px 20px 20px;
  }
`;

const InferFormLabel = styled.div`
  color: #fff;
  font-family: Jura;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-align: left;
  margin-bottom: 10px;
`;

const InferFormFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  margin-top: 20px;
  text-align: center;
`;

const FormTabsContainer = styled.div`
  width: 100%;
  height: auto;
  margin-top: -15px;
  margin-bottom: 10px;

  @media only screen and (max-width: 600px) {
    margin-top: -40px;
  }
`;

// const FormTabsTitle = styled.div`
//   color: var(--text-primary, #FFF);
//   text-align: center;
//   font-feature-settings: 'liga' off, 'clig' off;
//   font-family: Jura;
//   font-size: 20px;
//   font-style: normal;
//   font-weight: 400;
//   line-height: 133.4%;
//   margin-bottom: 20px;
// `;

const CharacterCount = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  font-size: 10px;
  font-weight: 400;
  font-family: 'Raleway';
`;

const AdvancedOptionsContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media only screen and (max-width: 600px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const ImproveLyricsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background-color: rgba(245, 225, 255, 0.12);
  color: rgba(245, 225, 255, 0.74);
  border: none;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 400;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  transition: all 0.2s ease;
`;

const HistoryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background-color: rgba(245, 225, 255, 0.12);
  color: rgba(245, 225, 255, 0.74);
  border-radius: 50%;
  font-size: 14px;
  font-weight: 400;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.3 : 1)};
  transition: all 0.2s ease;
  box-shadow: none;
  border: none;

  &:hover:not(:disabled) {
    background-color: rgba(245, 225, 255, 0.05);
  }
`;

const LyricsContainer = styled.div`
  position: relative;
  background-color: #180d23;
  padding: 20px;
  border-radius: 15px;
  margin-bottom: 10px;
`;

const LyricsButtonsFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  margin-bottom: 8px;
  position: absolute;
  bottom: 20px;
  left: 30px;
  right: 30px;
`;

const MainButton = styled.button`
  font-family: 'Jura';
  text-transform: uppercase;
  background: linear-gradient(180deg, #7D0EB5 0%, #4D0671 100%);
  color: white;
  border: none;
  border-radius: 15px;
  padding: 18px 50px;
  font-size: 28px;
  font-weight: 400;
  margin-top: 10px;
  cursor: pointer;
`;
